/**
 * Safe Update Guard — detecta operaciones sensibles donde no conviene recargar.
 * @module utils/criticalOperationGuard
 */

const POS_SESSION_KEY = 'mobile_pos_session_v1';
const OFFLINE_SALES_KEY = 'mobile_pos_offline_sales_v1';
export const CAJERO_VENTA_EN_PROCESO_KEY = 'mobile_pos_venta_en_proceso_v1';
const PAGO_EN_PROCESO_KEY = 'nexo_pago_en_proceso_v1';
const DESKTOP_POS_CART_KEY = 'nexo_desktop_pos_cart_v1';
const CAJA_OPERACION_KEY = 'nexo_caja_operacion_v1';
const DIRTY_FORMS_KEY = 'nexo_dirty_forms_v1';

/** Rutas sensibles (solo bloquean si hay estado activo asociado). */
export const SENSITIVE_ROUTE_PREFIXES = [
  '/cajero',
  '/ventas',
  '/punto-venta',
  '/compras',
  '/caja',
  '/clientes/',
  '/productos/nuevo',
  '/productos/editar/',
  '/stock/transferencias',
  '/produccion',
  '/configuracion/empresa',
  '/usuarios'
];

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readSessionFlag(key) {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(key, active) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (active) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function readDirtyForms() {
  try {
    const raw = sessionStorage.getItem(DIRTY_FORMS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeDirtyForms(map) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const keys = Object.keys(map).filter((k) => map[k]);
    if (keys.length === 0) sessionStorage.removeItem(DIRTY_FORMS_KEY);
    else sessionStorage.setItem(DIRTY_FORMS_KEY, JSON.stringify(map));
  } catch {
    // ignore
  }
}

function hasActiveCajeroCart() {
  const session = readJson(POS_SESSION_KEY);
  if (!session || typeof session !== 'object') return false;
  const carrito = Array.isArray(session.carrito) ? session.carrito : [];
  return carrito.length > 0;
}

function hasPendingOfflineSales() {
  const queue = readJson(OFFLINE_SALES_KEY);
  return Array.isArray(queue) && queue.length > 0;
}

function hasAnyFormDirty() {
  const map = readDirtyForms();
  return Object.values(map).some(Boolean);
}

function normalizePath(pathname = '') {
  const path = String(pathname || '').toLowerCase();
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}

function isCuentaCorrienteRoute(path) {
  return /\/clientes\/[^/]+\/cuenta-corriente$/.test(path);
}

function isTransferenciaRoute(path) {
  return path === '/stock/transferencias'
    || path === '/stock/transferencias/nueva'
    || /^\/stock\/transferencias\/[^/]+$/.test(path);
}

function isProduccionDetalleRoute(path) {
  return /^\/produccion\/[^/]+$/.test(path) && path !== '/produccion/nueva';
}

/**
 * Sincroniza el flag de venta/cobro en curso (cajero móvil y POS desktop).
 * @param {boolean} active
 */
export function setCajeroVentaEnProceso(active) {
  writeSessionFlag(CAJERO_VENTA_EN_PROCESO_KEY, active);
  setPagoEnProceso(active);
}

/** Cobro o confirmación de venta en curso. */
export function setPagoEnProceso(active) {
  writeSessionFlag(PAGO_EN_PROCESO_KEY, active);
}

/** Carrito activo en punto de venta desktop (/punto-venta, /ventas/nueva). */
export function setDesktopPosCartActive(active) {
  writeSessionFlag(DESKTOP_POS_CART_KEY, active);
}

/** Movimiento o verificación de caja en curso. */
export function setCajaOperacionEnCurso(active) {
  writeSessionFlag(CAJA_OPERACION_KEY, active);
}

/**
 * Marca un formulario como con cambios sin guardar.
 * @param {string} scope — ej. producto, compra, usuario, configuracion
 * @param {boolean} dirty
 */
export function setFormDirty(scope, dirty) {
  if (!scope) return;
  const map = readDirtyForms();
  if (dirty) map[scope] = true;
  else delete map[scope];
  writeDirtyForms(map);
}

export function isFormDirty(scope) {
  if (!scope) return false;
  return Boolean(readDirtyForms()[scope]);
}

/**
 * @returns {boolean} true si hay operación crítica en curso
 */
export function hasCriticalOperationActive() {
  if (typeof window === 'undefined') return false;

  if (hasPendingOfflineSales()) return true;
  if (readSessionFlag(CAJERO_VENTA_EN_PROCESO_KEY)) return true;
  if (readSessionFlag(PAGO_EN_PROCESO_KEY)) return true;
  if (readSessionFlag(DESKTOP_POS_CART_KEY)) return true;
  if (readSessionFlag(CAJA_OPERACION_KEY)) return true;
  if (hasAnyFormDirty()) return true;

  const path = normalizePath(window.location?.pathname || '');

  if ((path === '/cajero' || path.startsWith('/cajero/')) && hasActiveCajeroCart()) {
    return true;
  }

  if (isProduccionDetalleRoute(path)) return true;

  if (isCuentaCorrienteRoute(path) && readSessionFlag(PAGO_EN_PROCESO_KEY)) {
    return true;
  }

  if (isTransferenciaRoute(path) && isFormDirty('transferencia')) {
    return true;
  }

  return false;
}

export function describeCriticalOperation() {
  if (hasPendingOfflineSales()) return 'ventas offline pendientes';
  if (readSessionFlag(CAJERO_VENTA_EN_PROCESO_KEY) || readSessionFlag(PAGO_EN_PROCESO_KEY)) {
    return 'cobro en curso';
  }
  if (readSessionFlag(DESKTOP_POS_CART_KEY) || hasActiveCajeroCart()) {
    return 'venta activa en carrito';
  }
  if (readSessionFlag(CAJA_OPERACION_KEY)) return 'operación de caja';
  const dirty = Object.keys(readDirtyForms()).filter((k) => readDirtyForms()[k]);
  if (dirty.length > 0) return `formulario sin guardar (${dirty.join(', ')})`;
  const path = normalizePath(window.location?.pathname || '');
  if (isProduccionDetalleRoute(path)) return 'operación de producción';
  return 'operación en curso';
}

export function isSensitiveRoute(pathname = '') {
  const path = normalizePath(pathname);
  return SENSITIVE_ROUTE_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix));
}

/** Rutas POS/cajero donde el autorefresh agresivo de UX-SAFE-UPDATE-01 se mantiene. */
export function isPosProtectedRoute(pathname = '') {
  const path = normalizePath(pathname);
  return path === '/cajero' || path.startsWith('/cajero/')
    || path === '/punto-venta' || path.startsWith('/punto-venta/');
}
